import React, { useState } from 'react';
import axios from 'axios';
import { MessageSquare, Star, X, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ value, onChange, size = 28, testid }) => (
  <div className="flex space-x-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n)} className="transition-transform hover:scale-110" data-testid={`${testid}-${n}`}>
        <Star className={`${n <= value ? 'fill-orange text-orange' : 'text-muted-foreground'}`} size={size} />
      </button>
    ))}
  </div>
);

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 0, name: '', email: '', comment: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error('Please pick a star rating');
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/general`, {
        ...form,
        page: window.location.pathname,
      });
      toast.success('Thanks for your feedback!');
      setForm({ rating: 0, name: '', email: '', comment: '' });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher - left side to avoid platform badge overlap */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-[60] bg-navy hover:bg-orange text-white rounded-full p-4 shadow-lg transition-colors"
        aria-label="Send feedback"
        data-testid="feedback-launcher-btn"
      >
        <MessageSquare className="h-5 w-5" />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-end sm:items-center justify-center p-4" data-testid="feedback-modal">
          <div className="bg-white w-full sm:max-w-md rounded-lg shadow-2xl p-6 sm:mr-2">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-navy">Rate Your Experience</h3>
                <p className="text-xs text-muted-foreground">We read every response. Thank you!</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-muted-foreground" data-testid="feedback-close-btn"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <StarRating value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} testid="feedback-stars" />
              <input
                type="text"
                placeholder="Your name (optional)"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
                data-testid="feedback-name-input"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
                data-testid="feedback-email-input"
              />
              <textarea
                placeholder="What can we improve?"
                rows={3}
                value={form.comment}
                onChange={e => setForm({ ...form, comment: e.target.value })}
                className="w-full px-3 py-2 rounded border border-input bg-background text-sm"
                data-testid="feedback-comment-input"
              />
              <button
                type="submit"
                disabled={submitting || !form.rating}
                className="w-full px-4 py-2 rounded bg-orange hover:bg-orange/90 text-white text-sm font-semibold inline-flex items-center justify-center disabled:opacity-50"
                data-testid="feedback-submit-btn"
              >
                <Send className="h-4 w-4 mr-2" /> {submitting ? 'Sending…' : 'Send Feedback'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export { StarRating };
export default FeedbackWidget;
